import {
  DayOfWeek,
  OrderThrottleConfig,
  OrderType,
  ThrottleSetting,
  ThrottleSettingValues,
} from 'api/ordering-service/ordering-service.types';
import { Option } from 'util/multi-select';
import i18n from 'app-i18n';
import { DateTime, Interval } from 'luxon';
import { v4 as uuidv4 } from 'uuid';
import { blue, green } from '@mui/material/colors';
import { EventApi, EventInput } from '@fullcalendar/core/index.js';
import { DUMMY_EVENT_ID, dayOfWeekMap } from '../kitchen-capacity.utils';
import { daysOfWeekOptions } from 'components/forms/fields/days-multiple-select-field/days-multiple-select-field.utils';

export type PreparationTimeOrderType = OrderType;

export type ExtendedPropsType = {
  orderType: OrderType;
  eventType: 'PreparationTime';
  throttleSettings: ThrottleSettingValues;
};

export type SetPreparationTimeDrawerDataProps = {
  id?: string;
  title: string;
  start: DateTime;
  end: DateTime;
  throttleSettings: ThrottleSettingValues[];
};

export type SetPreparationTimeFormDataProps = {
  eventId?: string;
  daysOfWeek?: Option[];
  dateRange?: string[];
  timeRange?: string[];
  orderType?: OrderType[];
  throttleSettings?: ThrottleSettingValues[];
};

export const convertThrottleSettingsToEventInput = (
  data: OrderThrottleConfig[],
): EventInput[] => {
  return data.flatMap(item => {
    const orderType = item.applicableOrderTypes[0];

    return Object.entries(item.throttleSettings).flatMap(([day, slots]) => {
      return (slots as ThrottleSetting[]).map(slot => ({
        id: uuidv4(),
        title:
          orderType.charAt(0).toUpperCase() + orderType.slice(1).toLowerCase(),
        daysOfWeek: [dayOfWeekMap[day]],
        startTime: slot.timeSlot.startTime,
        endTime: slot.timeSlot.endTime,
        extendedProps: {
          orderType,
          eventType: 'PreparationTime',
          throttleSettings: slot.throttleSettings.map(setting => ({
            ...setting,
            id: uuidv4(), // Generate a unique identifier
            orderAmount: setting.orderAmount / 1000, // transform currency
          })),
        },
        color: orderType === 'PICKUP' ? blue[500] : green[500],
      }));
    });
  });
};

export const convertEventApiToRecurring = (event: EventApi): EventInput => {
  const startDateTime = DateTime.fromJSDate(event.start);
  const endDateTime = DateTime.fromJSDate(event.end);

  return {
    id: event.id,
    title: event.title,
    daysOfWeek: [(startDateTime.weekday % 7).toString()],
    startTime: startDateTime.toFormat('HH:mm:ss'),
    endTime: endDateTime.toFormat('HH:mm:ss'),
    extendedProps: event.extendedProps,
    color: event.backgroundColor,
  };
};

export const recurringEventsToOrderThrottleConfig = (events = []) => {
  const filteredEvents = events?.filter(
    event => event.extendedProps.eventType !== 'LocationHours',
  );

  if (filteredEvents.length === 0) {
    return [];
  }

  const orderThrottleConfig = filteredEvents.reduce((acc, event) => {
    const dayOfWeek = event.daysOfWeek[0];
    const day = Object.keys(dayOfWeekMap).find(
      key => dayOfWeekMap[key] === String(dayOfWeek),
    ) as DayOfWeek;

    const { orderType } = event.extendedProps;
    const timeSlot = {
      timeSlot: {
        startTime: event.startTime,
        endTime: event.endTime,
      },
      throttleSettings: event.extendedProps.throttleSettings.map(
        ({ id, ...setting }) => ({
          ...setting,
          orderAmount: setting.orderAmount * 1000, // reTransform currency
        }),
      ),
    };

    // Find or create an entry for this orderType
    let orderTypeEntry = acc.find(o =>
      o.applicableOrderTypes.includes(orderType),
    );
    if (!orderTypeEntry) {
      orderTypeEntry = {
        throttleSettings: {},
        applicableOrderTypes: [orderType],
      };
      acc.push(orderTypeEntry);
    }

    // Add time slot for the day of the week
    orderTypeEntry.throttleSettings[day] = [];
    orderTypeEntry.throttleSettings[day].push(timeSlot);

    return acc;
  }, []);

  return orderThrottleConfig;
};

export const convertSetPreparationTimeFormDataToEventSource = (
  data: SetPreparationTimeFormDataProps,
): EventInput[] => {
  const { daysOfWeek, timeRange, orderType, throttleSettings } = data;
  const startTime = DateTime.fromISO(timeRange[0]).toFormat('HH:mm:ss');
  const endTime = DateTime.fromISO(timeRange[1]).toFormat('HH:mm:ss');

  return daysOfWeek.flatMap(day => {
    return orderType.map(type => {
      return {
        id: uuidv4(),
        title:
          type === 'PICKUP'
            ? i18n.t('KitchenCapacity.eventTitles.preparationTime.pickup')
            : i18n.t('KitchenCapacity.eventTitles.preparationTime.delivery'),
        daysOfWeek: [dayOfWeekMap[day.label.toUpperCase()]],
        startTime,
        endTime,
        extendedProps: {
          orderType: type,
          eventType: 'PreparationTime',
          throttleSettings,
        },
        color: type === 'PICKUP' ? blue[500] : green[500],
      };
    });
  });
};

export const filterPreparationTimeOverlappedEvents = (
  data: SetPreparationTimeFormDataProps,
  existingEvents: EventInput[],
): EventInput[] => {
  const { eventId, daysOfWeek, timeRange, orderType } = data;

  if (!timeRange.every(time => DateTime.fromISO(time).isValid)) {
    return [];
  }

  const [startTime, endTime] = timeRange.map(time => {
    const { hour, minute, second, millisecond } = DateTime.fromISO(time);
    return DateTime.fromObject({ hour, minute, second, millisecond });
  });

  const inputInterval = Interval.fromDateTimes(startTime, endTime);

  // Filter events from eventArray based on matching day of week and order type
  return existingEvents.filter(event => {
    const eventStartTime = DateTime.fromFormat(event.startTime, 'HH:mm:ss');
    const eventEndTime = DateTime.fromFormat(event.endTime, 'HH:mm:ss');

    return (
      ![eventId, DUMMY_EVENT_ID].includes(event.id) &&
      event.extendedProps.eventType !== 'LocationHours' &&
      orderType.includes(event.extendedProps.orderType) &&
      (daysOfWeek[0]?.id === 'ALL' ||
        daysOfWeek.some(
          day =>
            day.label.toUpperCase() ===
            Object.keys(dayOfWeekMap)[event.daysOfWeek[0]],
        )) &&
      DateTime.fromISO(event.startTime) <= DateTime.fromISO(event.endTime) &&
      Interval.fromDateTimes(eventStartTime, eventEndTime).overlaps(
        inputInterval,
      )
    );
  });
};

export const createPreparationTimeDummyEvent = (
  start: DateTime,
  end: DateTime,
): EventInput => {
  return {
    id: DUMMY_EVENT_ID,
    title: i18n.t('KitchenCapacity.eventTitles.preparationTime.pickup'),
    start: start.toJSDate(),
    end: end.toJSDate(),
    extendedProps: {
      orderType: 'PICKUP',
      eventType: 'PreparationTime',
      throttleSettings: [
        {
          id: uuidv4(), // Generate a unique identifier
          orderAmount: 0,
          processingTime: 1,
          // TODO: This logic will be changed in a future story.
          displayMessage: {
            EN: 'We will need a little more time to prepare your Order. Please select a new time. Estimated prep time for orders exceeding $0.00 is 1 minute.',
          },
        },
      ],
    },
    color: blue[500],
  };
};

export const convertToSetPreparationTimeFormData = ({
  id,
  title,
  start,
  end,
  throttleSettings,
}: SetPreparationTimeDrawerDataProps): SetPreparationTimeFormDataProps => {
  let orderType: OrderType[] = [];

  const dayOfWeek = start?.setLocale('en-US').weekdayShort?.toUpperCase();

  const daysOfWeek = [
    {
      id: dayOfWeek,
      label: daysOfWeekOptions().find(option => option.id === dayOfWeek)?.label,
    },
  ];

  const dateRange = [start.startOf('day').toISO(), end.endOf('day').toISO()];

  const timeRange = [start.toISO(), end.toISO()];

  switch (title) {
    case i18n.t('KitchenCapacity.eventTitles.preparationTime.pickup'):
      orderType = ['PICKUP'];
      break;
    case i18n.t('KitchenCapacity.eventTitles.preparationTime.delivery'):
      orderType = ['DELIVERY'];
      break;
  }

  return {
    eventId: id ?? null,
    orderType,
    daysOfWeek,
    dateRange,
    timeRange,
    throttleSettings,
  };
};
